(() => {
  'use strict';

  const INCHES_PER_MILE = 63360;
  const MM_PER_INCH = 25.4;
  const SPEEDS = [30, 40, 50, 60, 70, 80, 90];

  const round = (value, places = 1) => {
    const factor = 10 ** places;
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  };

  const cleanNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const signed = (value, places = 1, suffix = '') => {
    const number = round(value, places);
    const sign = number > 0 ? '+' : '';
    return `${sign}${number.toFixed(places)}${suffix}`;
  };

  const trimZeros = (value, maxPlaces = 2) => {
    const number = round(value, maxPlaces);
    return Number.isInteger(number) ? String(number) : String(number).replace(/0+$/, '').replace(/\.$/, '');
  };

  function parseSizeString(input) {
    if (!input) return null;
    const normalized = String(input)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/ZR/g, 'R');

    const metric = normalized.match(/^(\d{3})\/(\d{2,3})\/?R?(\d{2}(?:\.5)?)$/);
    if (metric) {
      return {
        format: 'metric',
        width: cleanNumber(metric[1]),
        aspect: cleanNumber(metric[2]),
        rim: cleanNumber(metric[3])
      };
    }

    const flotation = normalized.match(/^(\d{2}(?:\.\d+)?)X(\d{1,2}(?:\.\d+)?)R?(\d{2}(?:\.5)?)$/);
    if (flotation) {
      return {
        format: 'flotation',
        diameter: cleanNumber(flotation[1]),
        width: cleanNumber(flotation[2]),
        rim: cleanNumber(flotation[3])
      };
    }

    return null;
  }

  function calculateSize(size) {
    if (!size || !size.format) return null;

    let diameter;
    let width;
    let sidewall;
    let rim;

    if (size.format === 'metric') {
      width = cleanNumber(size.width) / MM_PER_INCH;
      rim = cleanNumber(size.rim);
      sidewall = width * (cleanNumber(size.aspect) / 100);
      diameter = rim + (sidewall * 2);
    } else {
      diameter = cleanNumber(size.diameter);
      width = cleanNumber(size.width);
      rim = cleanNumber(size.rim);
      sidewall = (diameter - rim) / 2;
    }

    if (![diameter, width, sidewall, rim].every(Number.isFinite) || diameter <= 0 || width <= 0 || sidewall <= 0 || rim <= 0) {
      return null;
    }

    const circumference = diameter * Math.PI;
    const revsPerMile = INCHES_PER_MILE / circumference;
    const metricWidth = width * MM_PER_INCH;
    const metricAspect = (sidewall / width) * 100;

    return {
      diameter,
      width,
      sidewall,
      rim,
      circumference,
      revsPerMile,
      metricWidth,
      metricAspect,
      label: formatSizeLabel(size),
      metricEquivalent: `${Math.round(metricWidth / 5) * 5}/${Math.round(metricAspect / 5) * 5}R${trimZeros(rim, 1)}`,
      flotationEquivalent: `${trimZeros(diameter, 1)}X${Number(width).toFixed(1)}R${trimZeros(rim, 1)}`
    };
  }

  function formatSizeLabel(size) {
    if (!size) return '—';
    if (size.format === 'metric') {
      return `${Math.round(cleanNumber(size.width))}/${Math.round(cleanNumber(size.aspect))}R${trimZeros(cleanNumber(size.rim), 1)}`;
    }
    return `${trimZeros(cleanNumber(size.diameter), 1)}X${cleanNumber(size.width).toFixed(2)}R${trimZeros(cleanNumber(size.rim), 1)}`;
  }

  function convertSize(size, format) {
    const calculated = calculateSize(size);
    if (!calculated) return size;

    if (format === 'metric') {
      return {
        format: 'metric',
        width: Math.max(105, Math.min(455, Math.round(calculated.metricWidth / 5) * 5)),
        aspect: Math.max(20, Math.min(100, Math.round(calculated.metricAspect / 5) * 5)),
        rim: calculated.rim
      };
    }

    return {
      format: 'flotation',
      diameter: round(calculated.diameter * 2, 0) / 2,
      width: round(calculated.width * 2, 0) / 2,
      rim: calculated.rim
    };
  }

  function compareSizes(sizeOne, sizeTwo) {
    const one = calculateSize(sizeOne);
    const two = calculateSize(sizeTwo);
    if (!one || !two) return null;

    const diameterDifference = two.diameter - one.diameter;
    const diameterPercent = (diameterDifference / one.diameter) * 100;
    const widthDifference = two.width - one.width;
    const sidewallDifference = two.sidewall - one.sidewall;
    const circumferenceDifference = two.circumference - one.circumference;
    const revsDifference = two.revsPerMile - one.revsPerMile;
    const revsPercent = (revsDifference / one.revsPerMile) * 100;
    const speedRatio = two.diameter / one.diameter;

    return {
      one,
      two,
      diameterDifference,
      diameterPercent,
      widthDifference,
      widthPercent: (widthDifference / one.width) * 100,
      sidewallDifference,
      sidewallPercent: (sidewallDifference / one.sidewall) * 100,
      circumferenceDifference,
      circumferencePercent: (circumferenceDifference / one.circumference) * 100,
      revsDifference,
      revsPercent,
      speedRatio,
      clearanceChange: diameterDifference / 2,
      actualSpeeds: SPEEDS.map((speed) => ({ indicated: speed, actual: speed * speedRatio }))
    };
  }

  function serializeSize(size) {
    if (!size) return '';
    return size.format === 'metric'
      ? `m:${cleanNumber(size.width)}:${cleanNumber(size.aspect)}:${cleanNumber(size.rim)}`
      : `f:${cleanNumber(size.diameter)}:${cleanNumber(size.width)}:${cleanNumber(size.rim)}`;
  }

  function deserializeSize(value) {
    if (!value) return null;
    const parts = String(value).split(':');
    if (parts.length !== 4) return null;
    if (parts[0] === 'm') {
      return { format: 'metric', width: cleanNumber(parts[1]), aspect: cleanNumber(parts[2]), rim: cleanNumber(parts[3]) };
    }
    if (parts[0] === 'f') {
      return { format: 'flotation', diameter: cleanNumber(parts[1]), width: cleanNumber(parts[2]), rim: cleanNumber(parts[3]) };
    }
    return null;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  class SMGTireSizeLab extends BaseElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.initial = {
        one: parseSizeString(this.dataset.initialSizeOne) || { format: 'metric', width: 275, aspect: 35, rim: 20 },
        two: parseSizeString(this.dataset.initialSizeTwo) || { format: 'metric', width: 305, aspect: 35, rim: 20 },
        front: parseSizeString(this.dataset.initialSizeOne) || { format: 'metric', width: 275, aspect: 35, rim: 20 },
        rear: { format: 'metric', width: 305, aspect: 30, rim: 20 }
      };

      this.sizes = {
        one: { ...this.initial.one },
        two: { ...this.initial.two },
        front: { ...this.initial.front },
        rear: { ...this.initial.rear }
      };

      this.searchBase = this.dataset.searchBase || '/search?type=product&q=';
      this.defaultAxleRatio = cleanNumber(this.dataset.initialAxleRatio, 3.73);
      this.accent = '#de1f26';
      this.ink = '#111111';

      if (typeof window !== 'undefined') {
        const styles = window.getComputedStyle(this);
        this.accent = styles.getPropertyValue('--smg-accent').trim() || '#de1f26';
        this.ink = styles.getPropertyValue('--smg-ink').trim() || '#111111';
      }

      this.loadFromUrl();
      const axleInput = this.querySelector('[data-axle-ratio]');
      if (axleInput) axleInput.value = String(this.defaultAxleRatio);
      this.populateAlternateWheelSelect();
      this.renderAllFields();
      this.bindEvents();
      this.calculateComparison();
      this.calculateAwd();
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-tab-target]');
        if (tab) {
          this.activateTab(tab.dataset.tabTarget);
          return;
        }

        const formatButton = event.target.closest('[data-format-button]');
        if (formatButton) {
          const scope = formatButton.dataset.formatButton;
          const format = formatButton.dataset.format;
          this.sizes[scope] = convertSize(this.sizes[scope], format);
          this.renderFields(scope);
          this.syncFormatButtons(scope);
          this.updateForScope(scope);
          return;
        }

        const action = event.target.closest('[data-action]');
        if (action) {
          this.handleAction(action.dataset.action);
          return;
        }

        const alternate = event.target.closest('[data-alternate-size]');
        if (alternate) {
          const parsed = parseSizeString(alternate.dataset.alternateSize);
          if (parsed) {
            this.sizes.two = parsed;
            this.renderFields('two');
            this.syncFormatButtons('two');
            this.calculateComparison(true);
          }
        }
      });

      this.addEventListener('change', (event) => {
        const field = event.target.closest('[data-tire-field]');
        if (field) {
          const scope = field.dataset.scope;
          const property = field.dataset.tireField;
          this.sizes[scope][property] = cleanNumber(field.value, this.sizes[scope][property]);
          this.updateSizeLabel(scope);
          this.updateForScope(scope);
          return;
        }

        if (event.target.matches('[data-alternate-wheel]')) {
          this.renderAlternates();
          return;
        }

        if (event.target.matches('[data-axle-ratio]')) {
          this.calculateComparison();
        }
      });

      this.addEventListener('input', (event) => {
        const field = event.target.closest('input[data-tire-field]');
        if (field) {
          const scope = field.dataset.scope;
          const property = field.dataset.tireField;
          this.sizes[scope][property] = cleanNumber(field.value, this.sizes[scope][property]);
          this.updateSizeLabel(scope);
        }
      });
    }

    updateForScope(scope) {
      if (scope === 'one' || scope === 'two') {
        this.calculateComparison();
      } else {
        this.calculateAwd();
      }
    }

    activateTab(name) {
      this.querySelectorAll('[data-tab-target]').forEach((button) => {
        const active = button.dataset.tabTarget === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
        const active = panel.dataset.tabPanel === name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    handleAction(action) {
      switch (action) {
        case 'swap': {
          const temporary = this.sizes.one;
          this.sizes.one = this.sizes.two;
          this.sizes.two = temporary;
          this.renderFields('one');
          this.renderFields('two');
          this.syncFormatButtons('one');
          this.syncFormatButtons('two');
          this.calculateComparison(true);
          break;
        }
        case 'reset':
          this.sizes.one = { ...this.initial.one };
          this.sizes.two = { ...this.initial.two };
          this.renderFields('one');
          this.renderFields('two');
          this.syncFormatButtons('one');
          this.syncFormatButtons('two');
          const axle = this.querySelector('[data-axle-ratio]');
          if (axle) axle.value = String(this.defaultAxleRatio);
          this.calculateComparison(true);
          break;
        case 'calculate':
          this.calculateComparison(true);
          break;
        case 'calculate-awd':
          this.calculateAwd(true);
          break;
        case 'copy':
          this.copyResults();
          break;
        case 'print':
          if (typeof window !== 'undefined') window.print();
          break;
        default:
          break;
      }
    }

    renderAllFields() {
      ['one', 'two', 'front', 'rear'].forEach((scope) => {
        this.renderFields(scope);
        this.syncFormatButtons(scope);
      });
    }

    renderFields(scope) {
      const container = this.querySelector(`[data-size-fields="${scope}"]`);
      const size = this.sizes[scope];
      if (!container || !size) return;

      if (size.format === 'metric') {
        container.innerHTML = `
          <div class="smg-tire-lab__field-grid">
            ${this.selectField(scope, 'width', 'Width', 'mm', this.range(105, 455, 5), size.width)}
            ${this.selectField(scope, 'aspect', 'Aspect ratio', '%', this.range(20, 100, 5), size.aspect)}
            ${this.selectField(scope, 'rim', 'Wheel', 'inches', this.wheelValues(), size.rim)}
          </div>
        `;
      } else {
        container.innerHTML = `
          <div class="smg-tire-lab__field-grid">
            ${this.numberField(scope, 'diameter', 'Diameter', 'inches', size.diameter, 18, 60, .1)}
            ${this.numberField(scope, 'width', 'Width', 'inches', size.width, 3, 24, .05)}
            ${this.numberField(scope, 'rim', 'Wheel', 'inches', size.rim, 10, 32, .5)}
          </div>
        `;
      }

      this.updateSizeLabel(scope);
    }

    selectField(scope, property, label, unit, values, selected) {
      const options = values.map((value) => {
        const isSelected = Math.abs(cleanNumber(value) - cleanNumber(selected)) < .001;
        return `<option value="${value}"${isSelected ? ' selected' : ''}>${trimZeros(value, 1)}</option>`;
      }).join('');

      return `
        <label class="smg-tire-lab__field">
          <span>${label}</span>
          <div class="smg-tire-lab__field-control">
            <select data-scope="${scope}" data-tire-field="${property}" aria-label="${label}">${options}</select>
          </div>
          <small class="smg-tire-lab__field-unit">${unit}</small>
        </label>
      `;
    }

    numberField(scope, property, label, unit, value, min, max, step) {
      return `
        <label class="smg-tire-lab__field">
          <span>${label}</span>
          <div class="smg-tire-lab__field-control smg-tire-lab__field-control--input">
            <input type="number" value="${cleanNumber(value)}" min="${min}" max="${max}" step="${step}" inputmode="decimal" data-scope="${scope}" data-tire-field="${property}" aria-label="${label}">
          </div>
          <small class="smg-tire-lab__field-unit">${unit}</small>
        </label>
      `;
    }

    range(start, end, step) {
      const values = [];
      for (let value = start; value <= end + .001; value += step) values.push(round(value, 2));
      return values;
    }

    wheelValues() {
      const values = this.range(12, 32, 1);
      values.splice(values.indexOf(16) + 1, 0, 16.5);
      return values.sort((a, b) => a - b);
    }

    syncFormatButtons(scope) {
      this.querySelectorAll(`[data-format-button="${scope}"]`).forEach((button) => {
        const active = button.dataset.format === this.sizes[scope].format;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    updateSizeLabel(scope) {
      const label = this.querySelector(`[data-size-label="${scope}"]`);
      if (label) label.textContent = formatSizeLabel(this.sizes[scope]);
    }

    calculateComparison(scrollToResults = false) {
      const comparison = compareSizes(this.sizes.one, this.sizes.two);
      if (!comparison) return;
      this.comparison = comparison;

      this.updateSizeLabel('one');
      this.updateSizeLabel('two');
      this.renderCompatibility(comparison);
      this.renderVisualizer(comparison);
      this.renderQuickStats(comparison);
      this.renderSpecTable(comparison);
      this.renderSpeedGrid(comparison);
      this.renderAlternates();
      this.updateShopLink(comparison.two.label);
      this.updateUrl('compare');

      if (scrollToResults) {
        const results = this.querySelector('[data-results]');
        if (results && typeof results.scrollIntoView === 'function') {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    renderCompatibility(comparison) {
      const box = this.querySelector('[data-compatibility]');
      const title = this.querySelector('[data-compatibility-title]');
      const copy = this.querySelector('[data-compatibility-copy]');
      const difference = this.querySelector('[data-diameter-difference]');
      const icon = this.querySelector('[data-compatibility-icon]');
      if (!box || !title || !copy || !difference || !icon) return;

      const absolute = Math.abs(comparison.diameterPercent);
      let status = 'good';
      let headline = 'Excellent diameter match';
      let message = 'The labeled diameters are within 1%. Confirm wheel width, offset and physical clearance before purchase.';
      let symbol = '✓';

      if (absolute > 3) {
        status = 'warning';
        headline = 'Major diameter change';
        message = 'This change is greater than 3% and may require recalibration or other vehicle-specific changes. Verify fitment carefully.';
        symbol = '!';
      } else if (absolute > 1) {
        status = 'caution';
        headline = 'Noticeable diameter change';
        message = 'This falls within 3%, but speedometer, clearance and drivetrain effects should be reviewed for the exact vehicle.';
        symbol = 'i';
      }

      box.dataset.status = status;
      title.textContent = headline;
      copy.textContent = message;
      difference.textContent = `${absolute.toFixed(2)}%`;
      icon.textContent = symbol;
    }

    renderVisualizer(comparison) {
      const container = this.querySelector('[data-visualizer]');
      if (!container) return;

      const maxDiameter = Math.max(comparison.one.diameter, comparison.two.diameter);
      const maxWidth = Math.max(comparison.one.width, comparison.two.width);
      const radiusOne = (comparison.one.diameter / maxDiameter) * 116;
      const radiusTwo = (comparison.two.diameter / maxDiameter) * 116;
      const wheelRadiusOne = radiusOne * (comparison.one.rim / comparison.one.diameter);
      const wheelRadiusTwo = radiusTwo * (comparison.two.rim / comparison.two.diameter);
      const frontWidthOne = (comparison.one.width / maxWidth) * 150;
      const frontWidthTwo = (comparison.two.width / maxWidth) * 150;
      const centerX = 235;
      const groundY = 286;
      const centerYOne = groundY - radiusOne;
      const centerYTwo = groundY - radiusTwo;

      container.innerHTML = `
        <svg viewBox="0 0 760 340" role="img" aria-label="Scaled comparison of ${escapeHtml(comparison.one.label)} and ${escapeHtml(comparison.two.label)}">
          <defs>
            <filter id="smgShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="5" stdDeviation="5" flood-opacity=".12"/>
            </filter>
          </defs>
          <line x1="50" y1="${groundY}" x2="710" y2="${groundY}" stroke="#9ca3af" stroke-width="2"/>
          <text x="54" y="310" fill="#9ca3af" font-size="11" font-weight="800">GROUND LINE</text>

          <circle cx="${centerX}" cy="${centerYOne}" r="${radiusOne}" fill="rgba(104,113,125,.05)" stroke="#68717d" stroke-width="5" stroke-dasharray="9 6" filter="url(#smgShadow)"/>
          <circle cx="${centerX}" cy="${centerYOne}" r="${wheelRadiusOne}" fill="rgba(104,113,125,.09)" stroke="#68717d" stroke-width="3" stroke-dasharray="6 5"/>
          <circle cx="${centerX}" cy="${centerYTwo}" r="${radiusTwo}" fill="rgba(222,31,38,.06)" stroke="${this.accent}" stroke-width="5" filter="url(#smgShadow)"/>
          <circle cx="${centerX}" cy="${centerYTwo}" r="${wheelRadiusTwo}" fill="rgba(222,31,38,.10)" stroke="${this.accent}" stroke-width="3"/>
          <circle cx="${centerX}" cy="${centerYTwo}" r="7" fill="${this.ink}"/>

          <text x="${centerX}" y="26" fill="#111" font-size="13" text-anchor="middle" font-weight="900">SIDE VIEW OVERLAY</text>
          <text x="${centerX}" y="44" fill="#68717d" font-size="11" text-anchor="middle">${escapeHtml(comparison.one.label)} / ${escapeHtml(comparison.two.label)}</text>

          <text x="590" y="26" fill="#111" font-size="13" text-anchor="middle" font-weight="900">FRONT VIEW</text>
          <rect x="${590 - frontWidthOne / 2}" y="76" width="${frontWidthOne}" height="190" rx="25" fill="rgba(104,113,125,.05)" stroke="#68717d" stroke-width="4" stroke-dasharray="8 5"/>
          <rect x="${590 - frontWidthTwo / 2}" y="76" width="${frontWidthTwo}" height="190" rx="25" fill="rgba(222,31,38,.06)" stroke="${this.accent}" stroke-width="4"/>
          <line x1="${590 - frontWidthOne / 2}" y1="274" x2="${590 + frontWidthOne / 2}" y2="274" stroke="#68717d" stroke-width="2"/>
          <line x1="${590 - frontWidthTwo / 2}" y1="292" x2="${590 + frontWidthTwo / 2}" y2="292" stroke="${this.accent}" stroke-width="2"/>
          <text x="590" y="318" fill="#68717d" font-size="11" text-anchor="middle">${comparison.one.width.toFixed(1)} in → ${comparison.two.width.toFixed(1)} in</text>
        </svg>
      `;

      this.setText('[data-ride-height]', signed(comparison.clearanceChange, 2, ' in'));
      this.setText('[data-clearance]', signed(comparison.clearanceChange, 2, ' in'));
      this.setText('[data-width-change]', signed(comparison.widthDifference, 2, ' in'));
    }

    renderQuickStats(comparison) {
      const actual70 = 70 * comparison.speedRatio;
      this.setText('[data-speed-70]', `${actual70.toFixed(1)} mph`);
      this.setText('[data-revs-change]', signed(comparison.revsPercent, 1, '%'));
      this.setText('[data-diameter-inches]', signed(comparison.diameterDifference, 2, ' in'));

      const axleInput = this.querySelector('[data-axle-ratio]');
      const ratio = axleInput ? cleanNumber(axleInput.value, 0) : 0;
      const gearCard = this.querySelector('[data-gear-card]');
      if (gearCard) {
        const output = gearCard.querySelector('[data-effective-ratio]');
        if (ratio > 0) {
          output.textContent = (ratio * (comparison.one.diameter / comparison.two.diameter)).toFixed(2);
          gearCard.hidden = false;
        } else {
          output.textContent = '—';
        }
      }
    }

    renderSpecTable(comparison) {
      this.setText('[data-table-size-one]', comparison.one.label);
      this.setText('[data-table-size-two]', comparison.two.label);
      const tbody = this.querySelector('[data-spec-table]');
      if (!tbody) return;

      const rows = [
        ['Overall diameter', `${comparison.one.diameter.toFixed(2)} in`, `${comparison.two.diameter.toFixed(2)} in`, signed(comparison.diameterDifference, 2, ' in'), comparison.diameterDifference],
        ['Section width', `${comparison.one.width.toFixed(2)} in`, `${comparison.two.width.toFixed(2)} in`, signed(comparison.widthDifference, 2, ' in'), comparison.widthDifference],
        ['Sidewall height', `${comparison.one.sidewall.toFixed(2)} in`, `${comparison.two.sidewall.toFixed(2)} in`, signed(comparison.sidewallDifference, 2, ' in'), comparison.sidewallDifference],
        ['Circumference', `${comparison.one.circumference.toFixed(2)} in`, `${comparison.two.circumference.toFixed(2)} in`, signed(comparison.circumferenceDifference, 2, ' in'), comparison.circumferenceDifference],
        ['Revolutions per mile', comparison.one.revsPerMile.toFixed(1), comparison.two.revsPerMile.toFixed(1), signed(comparison.revsDifference, 1), comparison.revsDifference],
        ['Wheel diameter', `${trimZeros(comparison.one.rim, 1)} in`, `${trimZeros(comparison.two.rim, 1)} in`, signed(comparison.two.rim - comparison.one.rim, 1, ' in'), comparison.two.rim - comparison.one.rim],
        ['Approx. flotation equivalent', comparison.one.flotationEquivalent, comparison.two.flotationEquivalent, '—', 0],
        ['Approx. metric equivalent', comparison.one.metricEquivalent, comparison.two.metricEquivalent, '—', 0]
      ];

      tbody.innerHTML = rows.map(([label, one, two, difference, raw]) => {
        const className = raw > 0 ? 'is-positive' : raw < 0 ? 'is-negative' : '';
        return `<tr><td>${label}</td><td>${one}</td><td>${two}</td><td class="${className}">${difference}</td></tr>`;
      }).join('');
    }

    renderSpeedGrid(comparison) {
      const container = this.querySelector('[data-speed-grid]');
      if (!container) return;
      container.innerHTML = comparison.actualSpeeds.map(({ indicated, actual }) => `
        <div class="smg-tire-lab__speed-item">
          <span>${indicated} indicated</span>
          <strong>${actual.toFixed(1)}</strong>
          <small>mph actual</small>
        </div>
      `).join('');
    }

    populateAlternateWheelSelect() {
      const select = this.querySelector('[data-alternate-wheel]');
      if (!select) return;
      const values = [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 26, 28, 30];
      select.innerHTML = values.map((value) => `<option value="${value}">${value}-inch wheel</option>`).join('');
      const proposed = calculateSize(this.sizes.two);
      const current = calculateSize(this.sizes.one);
      let target = proposed ? Math.round(proposed.rim) : 20;
      if (current && Math.abs(target - Math.round(current.rim)) < .01) {
        const smaller = Math.round(current.rim) - 1;
        const larger = Math.round(current.rim) + 1;
        target = values.includes(smaller) ? smaller : larger;
      }
      if (values.includes(target)) select.value = String(target);
    }

    renderAlternates() {
      const container = this.querySelector('[data-alternate-list]');
      const select = this.querySelector('[data-alternate-wheel]');
      if (!container || !select || !this.comparison) return;

      const rim = cleanNumber(select.value, this.comparison.two.rim);
      const target = this.comparison.one.diameter;
      const currentWidth = this.comparison.one.metricWidth;
      const suggestions = [];

      for (let width = 155; width <= 445; width += 5) {
        for (let aspect = 20; aspect <= 95; aspect += 5) {
          const size = { format: 'metric', width, aspect, rim };
          const calculated = calculateSize(size);
          if (!calculated) continue;
          const difference = ((calculated.diameter - target) / target) * 100;
          if (Math.abs(difference) <= 3.01) {
            suggestions.push({
              size,
              calculated,
              difference,
              widthDistance: Math.abs(width - currentWidth)
            });
          }
        }
      }

      suggestions.sort((a, b) => {
        const diameterSort = Math.abs(a.difference) - Math.abs(b.difference);
        if (Math.abs(diameterSort) > .001) return diameterSort;
        return a.widthDistance - b.widthDistance;
      });

      const unique = [];
      const seen = new Set();
      for (const suggestion of suggestions) {
        const label = formatSizeLabel(suggestion.size);
        if (seen.has(label)) continue;
        seen.add(label);
        unique.push(suggestion);
        if (unique.length >= 12) break;
      }

      if (!unique.length) {
        container.innerHTML = '<div class="smg-tire-lab__alternate-empty">No close calculated matches found for this wheel diameter.</div>';
        return;
      }

      container.innerHTML = unique.map(({ size, calculated, difference }) => `
        <button type="button" class="smg-tire-lab__alternate-item" data-alternate-size="${formatSizeLabel(size)}">
          <strong>${formatSizeLabel(size)}</strong>
          <span>${calculated.diameter.toFixed(2)} in diameter • ${signed(difference, 2, '%')}</span>
        </button>
      `).join('');
    }

    updateShopLink(label) {
      const link = this.querySelector('[data-shop-link]');
      if (!link) return;
      link.href = `${this.searchBase}${encodeURIComponent(label)}`;
      link.setAttribute('aria-label', `Shop tires matching ${label}`);
    }

    calculateAwd(scrollToResults = false) {
      const front = calculateSize(this.sizes.front);
      const rear = calculateSize(this.sizes.rear);
      if (!front || !rear) return;

      const differenceInches = rear.diameter - front.diameter;
      const differencePercent = (Math.abs(differenceInches) / front.diameter) * 100;
      const revsDifference = Math.abs(rear.revsPerMile - front.revsPerMile);
      const box = this.querySelector('[data-awd-compatibility]');
      const title = this.querySelector('[data-awd-title]');
      const copy = this.querySelector('[data-awd-copy]');
      const icon = this.querySelector('[data-awd-icon]');

      let status = 'good';
      let headline = 'Very close rolling diameter';
      let message = 'The calculated front-to-rear difference is 0.5% or less. Still verify the exact vehicle and tire manufacturer requirements.';
      let symbol = '✓';

      if (differencePercent > 1) {
        status = 'warning';
        headline = 'High front-to-rear variance';
        message = 'The calculated difference is greater than 1%. Do not assume this is acceptable for an AWD system without manufacturer confirmation.';
        symbol = '!';
      } else if (differencePercent > .5) {
        status = 'caution';
        headline = 'Verify the vehicle tolerance';
        message = 'The calculated variance is between 0.5% and 1%. Some systems may allow it; others may require a tighter match.';
        symbol = 'i';
      }

      if (box) box.dataset.status = status;
      if (title) title.textContent = headline;
      if (copy) copy.textContent = message;
      if (icon) icon.textContent = symbol;
      this.setText('[data-awd-difference]', `${differencePercent.toFixed(2)}%`);
      this.setText('[data-awd-front-diameter]', `${front.diameter.toFixed(2)} in`);
      this.setText('[data-awd-rear-diameter]', `${rear.diameter.toFixed(2)} in`);
      this.setText('[data-awd-revs]', `${revsDifference.toFixed(1)} rev/mi`);
      this.setText('[data-awd-inches]', signed(differenceInches, 2, ' in'));
      this.renderAwdVisual(front, rear);
      this.updateUrl('awd');

      if (scrollToResults) {
        const results = this.querySelector('[data-awd-results]');
        if (results && typeof results.scrollIntoView === 'function') {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    renderAwdVisual(front, rear) {
      const container = this.querySelector('[data-awd-visual]');
      if (!container) return;
      const maxDiameter = Math.max(front.diameter, rear.diameter);
      const frontRadius = (front.diameter / maxDiameter) * 104;
      const rearRadius = (rear.diameter / maxDiameter) * 104;
      const groundY = 252;

      container.innerHTML = `
        <svg viewBox="0 0 700 290" role="img" aria-label="Front and rear rolling diameter comparison">
          <line x1="50" y1="${groundY}" x2="650" y2="${groundY}" stroke="#9ca3af" stroke-width="2"/>
          <circle cx="205" cy="${groundY - frontRadius}" r="${frontRadius}" fill="rgba(104,113,125,.05)" stroke="#68717d" stroke-width="5"/>
          <circle cx="205" cy="${groundY - frontRadius}" r="${frontRadius * (front.rim / front.diameter)}" fill="#fff" stroke="#68717d" stroke-width="3"/>
          <circle cx="495" cy="${groundY - rearRadius}" r="${rearRadius}" fill="rgba(222,31,38,.06)" stroke="${this.accent}" stroke-width="5"/>
          <circle cx="495" cy="${groundY - rearRadius}" r="${rearRadius * (rear.rim / rear.diameter)}" fill="#fff" stroke="${this.accent}" stroke-width="3"/>
          <text x="205" y="278" fill="#68717d" font-size="13" text-anchor="middle" font-weight="900">FRONT • ${escapeHtml(front.label)}</text>
          <text x="495" y="278" fill="${this.accent}" font-size="13" text-anchor="middle" font-weight="900">REAR • ${escapeHtml(rear.label)}</text>
        </svg>
      `;
    }

    copyResults() {
      if (!this.comparison) return;
      const c = this.comparison;
      const axleInput = this.querySelector('[data-axle-ratio]');
      const ratio = axleInput ? cleanNumber(axleInput.value, 0) : 0;
      const effective = ratio > 0 ? (ratio * (c.one.diameter / c.two.diameter)).toFixed(2) : 'not entered';
      const url = typeof window !== 'undefined' ? window.location.href : '';
      const text = [
        'SMG Tire Size Calculator Results',
        `${c.one.label} → ${c.two.label}`,
        `Diameter: ${c.one.diameter.toFixed(2)} in → ${c.two.diameter.toFixed(2)} in (${signed(c.diameterPercent, 2, '%')})`,
        `Width: ${c.one.width.toFixed(2)} in → ${c.two.width.toFixed(2)} in (${signed(c.widthDifference, 2, ' in')})`,
        `Ground clearance change: ${signed(c.clearanceChange, 2, ' in')}`,
        `Actual speed at 70 indicated: ${(70 * c.speedRatio).toFixed(1)} mph`,
        `Effective axle ratio: ${effective}`,
        url
      ].filter(Boolean).join('\n');

      this.copyText(text)
        .then(() => this.showToast('Results copied to your clipboard.'))
        .catch(() => this.showToast('Could not copy automatically. Please use your browser copy command.'));
    }

    copyText(text) {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }
      return new Promise((resolve, reject) => {
        if (typeof document === 'undefined') return reject(new Error('Document unavailable'));
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textarea);
          successful ? resolve() : reject(new Error('Copy failed'));
        } catch (error) {
          document.body.removeChild(textarea);
          reject(error);
        }
      });
    }

    showToast(message) {
      if (typeof document === 'undefined') return;
      const existing = document.querySelector('.smg-tire-lab__toast');
      if (existing) existing.remove();
      const toast = document.createElement('div');
      toast.className = 'smg-tire-lab__toast';
      toast.setAttribute('role', 'status');
      toast.textContent = message;
      document.body.appendChild(toast);
      window.setTimeout(() => toast.remove(), 2600);
    }

    updateUrl(mode) {
      if (typeof window === 'undefined' || !window.history || !window.URL) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('smg_s1', serializeSize(this.sizes.one));
        url.searchParams.set('smg_s2', serializeSize(this.sizes.two));
        url.searchParams.set('smg_front', serializeSize(this.sizes.front));
        url.searchParams.set('smg_rear', serializeSize(this.sizes.rear));
        const axle = this.querySelector('[data-axle-ratio]');
        if (axle && cleanNumber(axle.value, 0) > 0) url.searchParams.set('smg_axle', axle.value);
        url.searchParams.set('smg_mode', mode);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        // URL updates are a convenience; calculation should never depend on them.
      }
    }

    loadFromUrl() {
      if (typeof window === 'undefined' || !window.URL) return;
      try {
        const url = new URL(window.location.href);
        const mappings = [
          ['smg_s1', 'one'],
          ['smg_s2', 'two'],
          ['smg_front', 'front'],
          ['smg_rear', 'rear']
        ];
        mappings.forEach(([parameter, scope]) => {
          const parsed = deserializeSize(url.searchParams.get(parameter));
          if (parsed) this.sizes[scope] = parsed;
        });
        const axle = url.searchParams.get('smg_axle');
        if (axle) this.defaultAxleRatio = cleanNumber(axle, this.defaultAxleRatio);
        const mode = url.searchParams.get('smg_mode');
        if (mode === 'awd' || mode === 'learn') {
          window.setTimeout(() => this.activateTab(mode), 0);
        }
      } catch (error) {
        // Ignore malformed or unavailable URLs.
      }
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    }
  }

  if (typeof customElements !== 'undefined' && !customElements.get('smg-tire-size-lab')) {
    customElements.define('smg-tire-size-lab', SMGTireSizeLab);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      parseSizeString,
      calculateSize,
      compareSizes,
      formatSizeLabel,
      convertSize,
      serializeSize,
      deserializeSize,
      round
    };
  }
})();
